import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { db } from '../lib/firebase';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { Package, Clock, Truck, CheckCircle2, RotateCcw, ChevronDown, ChevronUp, ArrowLeft } from 'lucide-react';
import { Link, useLocation } from 'wouter';
import { motion, AnimatePresence } from 'motion/react';

export default function Orders() {
  const { user, loading: authLoading } = useAuth();
  const [, setLocation] = useLocation();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'orders'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsub = onSnapshot(q, (snapshot) => {
      setOrders(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    }, (err) => {
      console.error(err);
      setLoading(false);
    });

    return () => unsub();
  }, [user]);

  const filteredOrders = statusFilter === 'all' 
    ? orders 
    : orders.filter(o => o.status === statusFilter);

  if (authLoading || (user && loading)) {
    return <div className="min-h-[60vh] flex items-center justify-center">Loading your orders...</div>;
  }

  if (!user) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center p-4">
        <h1 className="text-2xl font-serif font-bold text-white mb-4">Please Login</h1>
        <p className="text-white/60 mb-8">Sign in to track your orders and spice deliveries.</p>
        <Link href="/" className="bg-spice-accent text-white px-8 py-3 uppercase tracking-widest text-xs font-bold rounded-2xl">Go Home</Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-6 pt-6 sm:pt-12 pb-32 animate-in fade-in duration-300">
      <div className="mb-6 sm:mb-8">
        <button 
          onClick={() => setLocation('/')}
          className="inline-flex items-center gap-2.5 text-[#1B120C] hover:text-spice-accent transition-all group bg-[#FAF5EB] px-4 sm:px-6 py-2.5 sm:py-3 border border-[rgba(27,18,12,0.1)] rounded-2xl shadow-sm hover:shadow-md min-h-[44px]"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="text-[10px] sm:text-xs uppercase font-black tracking-[0.2em]">Back to Shopping</span>
        </button>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-8">
        <div className="flex items-center gap-3 sm:gap-4">
           <div className="w-10 h-10 sm:w-12 sm:h-12 bg-spice-accent/10 rounded-2xl flex items-center justify-center border border-spice-accent/20">
              <Package className="w-5 h-5 sm:w-6 sm:h-6 text-spice-accent" />
           </div>
           <div>
              <h1 className="text-2xl sm:text-3xl font-serif font-bold text-[#1B120C]">Your Orders</h1>
              <p className="text-[#63574E] text-xs uppercase tracking-widest font-bold">Tracking {orders.length} deliveries</p>
           </div>
        </div>

        <div className="flex flex-col gap-1.5 w-full sm:w-auto">
          <label className="text-[9px] uppercase font-bold tracking-widest text-[#63574E]">Filter by Status</label>
          <select 
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="bg-[#FAF5EB] border border-[rgba(27,18,12,0.15)] p-2.5 sm:p-3 rounded-xl text-xs font-bold uppercase tracking-widest outline-none focus:border-spice-accent w-full sm:min-w-[200px] text-[#1B120C] min-h-[44px]"
          >
            <option value="all">All Orders</option>
            <option value="Received">Received</option>
            <option value="Picking & Packing">Processing</option>
            <option value="Ready for Dispatch">Ready for Dispatch</option>
            <option value="Completed">Completed</option>
            <option value="Returned">Returned</option>
          </select>
        </div>
      </div>

      {orders.length === 0 ? (
        <div className="bg-white/5 border border-white/10 p-12 text-center rounded-3xl">
           <p className="text-white mb-6 italic">You haven't placed any orders yet.</p>
           <Link href="/products" className="inline-block bg-spice-accent text-white px-8 py-3 uppercase tracking-widest text-xs font-bold rounded-2xl">Explore Spices</Link>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="bg-white/5 border border-white/10 p-12 text-center rounded-3xl">
           <p className="text-white mb-4">No orders found with status: <span className="font-bold uppercase text-spice-accent">{statusFilter}</span></p>
           <button 
             onClick={() => setStatusFilter('all')}
             className="text-spice-accent text-xs font-bold uppercase tracking-widest hover:underline"
           >
             Show all orders
           </button>
        </div>
      ) : (
        <div className="space-y-6">
          {filteredOrders.map((order) => {
            const isExpanded = expandedOrderId === order.id;
            
            return (
              <div 
                key={order.id} 
                className={`bg-[#1C1F26] border transition-all duration-300 rounded-3xl overflow-hidden ${isExpanded ? 'border-spice-accent shadow-lg shadow-spice-accent/10' : 'border-white/5 hover:border-white/10'}`}
              >
                {/* Order Header - Clickable */}
                <button 
                  onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                  className="w-full text-left p-6 flex flex-col md:flex-row justify-between items-center gap-4 hover:bg-white/5 transition-colors"
                >
                  <div className="flex items-center gap-6 w-full md:w-auto">
                    <div>
                        <div className="text-[10px] uppercase font-bold tracking-[0.2em] text-white/40 mb-1">Order Number</div>
                        <div className="font-serif font-bold text-lg text-white">#{order.orderNumber || order.id.slice(0,8)}</div>
                    </div>
                    <div className="h-10 w-px bg-white/10 hidden md:block"></div>
                    <div>
                        <div className="text-[10px] uppercase font-bold tracking-[0.2em] text-white/40 mb-1">Status</div>
                        <div className={`text-[10px] font-black px-3 py-1 inline-block uppercase tracking-widest rounded-lg ${
                          order.status === 'Completed' ? 'bg-emerald-600 text-white !text-white border border-emerald-500 shadow-sm' : 
                          order.status === 'Returned' ? 'bg-red-500/10 text-red-400 border border-red-500/20' :
                          'bg-white/10 text-white'
                        }`} style={order.status === 'Completed' ? { color: '#ffffff' } : {}}>
                          <span style={order.status === 'Completed' ? { color: '#ffffff' } : {}}>{order.status}</span>
                        </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between w-full md:w-auto md:gap-12">
                     <div className="flex gap-8">
                        <div>
                            <div className="text-[10px] uppercase font-bold tracking-[0.2em] text-white/40 mb-1">Placed On</div>
                            <div className="text-sm font-bold text-white">
                                {order.createdAt?.toDate ? order.createdAt.toDate().toLocaleDateString() : new Date().toLocaleDateString()}
                            </div>
                        </div>
                        <div>
                            <div className="text-[10px] uppercase font-bold tracking-[0.2em] text-white/40 mb-1">Total</div>
                            <div className="text-sm font-bold text-spice-accent">₹{order.totalAmount}</div>
                        </div>
                     </div>
                     <div className={`p-2 rounded-full border transition-all duration-300 ${isExpanded ? 'bg-spice-accent text-white border-spice-accent scale-110 rotate-180' : 'bg-white/5 text-white/40 border-white/10'}`}>
                        <ChevronDown className="w-4 h-4" />
                     </div>
                  </div>
                </button>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div 
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="overflow-hidden"
                    >
                      <div className="p-6 border-t border-white/5 bg-black/20 flex flex-col md:flex-row gap-10">
                        <div className="flex-1">
                            <div className="text-[10px] uppercase font-bold tracking-[0.2em] text-white/20 mb-6 flex items-center gap-2">
                                <span className="w-6 h-px bg-white/10"></span>
                                Order Summary
                            </div>
                            <div className="space-y-4">
                               {order.items?.map((item: any, idx: number) => (
                                 <div key={idx} className="flex justify-between items-center bg-white/5 p-4 rounded-2xl border border-white/5 group hover:border-spice-accent/30 transition-colors">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-[#252A34] rounded-xl flex items-center justify-center text-xs font-serif font-bold text-spice-accent border border-white/5">
                                            {item.name.charAt(0)}
                                        </div>
                                        <div>
                                            <div className="font-bold text-white text-sm">{item.name}</div>
                                            <div className="text-white/40 text-[10px] uppercase tracking-widest font-semibold">{item.selectedWeight} &bull; Qty: {item.quantity}</div>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="font-bold text-white text-sm">₹{item.price * item.quantity}</div>
                                        <div className="text-[10px] text-white/30">₹{item.price}/unit</div>
                                    </div>
                                 </div>
                               ))}
                            </div>
                            
                            <div className="mt-8 pt-6 border-t border-white/5 flex flex-col gap-3">
                                <div className="flex justify-between text-[11px] text-white/40 font-bold uppercase tracking-widest">
                                    <span>Subtotal</span>
                                    <span className="text-white">₹{order.totalAmount}</span>
                                </div>
                                <div className="flex justify-between text-[11px] text-white/40 font-bold uppercase tracking-widest">
                                    <span>Shipping</span>
                                    <span className="text-green-400 font-black">Free</span>
                                </div>
                                <div className="flex justify-between text-base font-black text-white mt-4 pt-4 border-t border-white/10">
                                    <span className="font-serif italic uppercase tracking-[0.1em]">Grand Total</span>
                                    <span className="text-spice-accent text-xl">₹{order.totalAmount}</span>
                                </div>
                            </div>
                        </div>

                        <div className="w-full md:w-72">
                            <div className="text-[10px] uppercase font-bold tracking-[0.2em] text-white/20 mb-6 flex items-center gap-2">
                                <span className="w-6 h-px bg-white/10"></span>
                                Delivery Timeline
                            </div>
                            
                            <div className="space-y-4 relative">
                               {/* Flow line */}
                               <div className="absolute left-[15px] top-4 bottom-4 w-[2px] bg-white/5"></div>
                               
                               <div className="flex items-start gap-4 relative z-10">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${order.status !== 'Received' ? 'bg-white text-black border-white' : 'bg-black border-spice-accent text-spice-accent shadow-lg shadow-spice-accent/20 scale-110'}`}>
                                     <Clock className="w-4 h-4" />
                                  </div>
                                  <div className="pt-1">
                                     <p className={`text-[10px] font-black uppercase tracking-widest leading-none ${order.status === 'Received' ? 'text-spice-accent' : 'text-white'}`}>Received</p>
                                     <p className="text-[9px] text-white/40 mt-1 uppercase font-semibold">Order confirmed</p>
                                  </div>
                               </div>

                               <div className="flex items-start gap-4 relative z-10">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                                    ['Picking & Packing', 'Ready for Dispatch', 'Completed', 'Returned'].includes(order.status) 
                                      ? 'bg-white text-black border-white' 
                                      : order.status === 'Picking & Packing' 
                                        ? 'bg-black border-spice-accent text-spice-accent shadow-lg shadow-spice-accent/20 scale-110'
                                        : 'bg-black border-white/10 text-white/20'
                                  }`}>
                                     <Package className="w-4 h-4" />
                                  </div>
                                  <div className="pt-1">
                                     <p className={`text-[10px] font-black uppercase tracking-widest leading-none ${order.status === 'Picking & Packing' ? 'text-spice-accent' : 'text-white'}`}>Processing</p>
                                     <p className="text-[9px] text-white/40 mt-1 uppercase font-semibold">Quality check</p>
                                  </div>
                               </div>

                               <div className="flex items-start gap-4 relative z-10">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                                    ['Ready for Dispatch', 'Completed', 'Returned'].includes(order.status) 
                                      ? 'bg-white text-black border-white' 
                                      : order.status === 'Ready for Dispatch' 
                                        ? 'bg-black border-spice-accent text-spice-accent shadow-lg shadow-spice-accent/20 scale-110'
                                        : 'bg-black border-white/10 text-white/20'
                                  }`}>
                                     <Truck className="w-4 h-4" />
                                  </div>
                                  <div className="pt-1">
                                     <p className={`text-[10px] font-black uppercase tracking-widest leading-none ${order.status === 'Ready for Dispatch' ? 'text-spice-accent' : 'text-white'}`}>Ready</p>
                                     <p className="text-[9px] text-white/40 mt-1 uppercase font-semibold">Awaiting courier</p>
                                  </div>
                               </div>

                               <div className="flex items-start gap-4 relative z-10">
                                  <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                                    order.status === 'Completed' 
                                      ? 'bg-green-500 border-green-500 text-white shadow-lg shadow-green-500/20' 
                                      : order.status === 'Returned'
                                        ? 'bg-red-500 border-red-500 text-white shadow-lg shadow-red-500/20'
                                        : 'bg-black border-white/10 text-white/20'
                                  }`}>
                                     {order.status === 'Returned' ? <RotateCcw className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                                  </div>
                                  <div className="pt-1">
                                     <p className={`text-[10px] font-black uppercase tracking-widest leading-none ${order.status === 'Completed' ? 'text-green-400' : order.status === 'Returned' ? 'text-red-400' : 'text-white'}`}>
                                       {order.status === 'Returned' ? 'Returned' : 'Delivered'}
                                     </p>
                                     <p className="text-[9px] text-white/40 mt-1 uppercase font-semibold">Final status</p>
                                  </div>
                               </div>
                            </div>
                            
                            {order.status !== 'Completed' && order.status !== 'Returned' && (
                               <div className="mt-8 p-4 bg-white/5 border-l-4 border-spice-accent rounded-r-xl">
                                  <p className="text-[9px] font-extrabold uppercase tracking-widest text-white/40 mb-1">Estimated Arrival</p>
                                  <p className="text-xs font-bold text-white">3-5 Business Days</p>
                               </div>
                            )}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
